import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Globe,
  Plus,
  Pause,
  Play,
  Trash2,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Link,
  RefreshCw,
  BarChart2,
  CalendarClock,
  FileText,
  Save,
  Search,
  X,
  Filter,
  ChevronLeft,
  ChevronRight,
  Lock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { scraperSourcesApi } from '../../../api/scraperSourcesApi';
import { CardGlowBackground, type CardGlowVariant } from '../../components/CardGlowBackground';
import { moderatorSchedulerApi } from '../../../api/schedulerApi';
import { scrapingApi } from '../../../api/scrapingApi';
import { scraperQuotaApi } from '../../../api/scraperQuotaApi';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useUiLocale } from '../../../contexts/UiLocaleContext';
import type {
  ClientScheduler,
  ModeratorScraperQuota,
  ScraperQuotaErrorDetail,
  ScraperSource,
  ScrapingStatus,
  SubscriptionTopic,
} from '../../../types/api';
import { topicChipStyle } from './TopicMultiSelect';
import { ImportSourcesCard } from './ImportSourcesCard';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

const GLASS_PANEL =
  'border border-white/50 bg-white/40 shadow-[0_8px_32px_rgba(15,23,42,0.08)]  backdrop-blur-xl backdrop-saturate-150';

type ScraperStatTone = 'purple' | 'green' | 'indigo';

function scraperStatGlass(tone: ScraperStatTone) {
  switch (tone) {
    case 'green':
      return {
        glow: 'emerald' as CardGlowVariant,
        card: 'ring-1 ring-emerald-200/45 shadow-[0_4px_20px_-6px_rgba(16,185,129,0.18)]',
        badge: 'border-emerald-200/70 bg-emerald-500/12 text-emerald-900',
        iconWrap: 'bg-emerald-500/15 ring-1 ring-emerald-200/50',
        iconColor: 'text-emerald-600',
        value: 'text-emerald-950',
      };
    case 'indigo':
      return {
        glow: 'indigo' as CardGlowVariant,
        card: 'ring-1 ring-indigo-200/45 shadow-[0_4px_20px_-6px_rgba(79,70,229,0.18)]',
        badge: 'border-indigo-200/70 bg-indigo-500/12 text-indigo-900',
        iconWrap: 'bg-indigo-500/15 ring-1 ring-indigo-200/50',
        iconColor: 'text-indigo-600',
        value: 'text-indigo-950',
      };
    default:
      return {
        glow: 'purple' as CardGlowVariant,
        card: 'ring-1 ring-purple-200/45 shadow-[0_4px_20px_-6px_rgba(147,51,234,0.18)]',
        badge: 'border-purple-200/70 bg-purple-500/12 text-purple-900',
        iconWrap: 'bg-purple-500/15 ring-1 ring-purple-200/50',
        iconColor: 'text-purple-600',
        value: 'text-purple-950',
      };
  }
}

interface ScraperStatCardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  tone: ScraperStatTone;
}

function ScraperStatCard({ label, value, icon: Icon, tone }: ScraperStatCardProps) {
  const glass = scraperStatGlass(tone);
  return (
    <Card className={`relative min-w-0 overflow-hidden gap-0 ${GLASS_PANEL} ${glass.card}`}>
      <CardGlowBackground variant={glass.glow} />
      <CardHeader className="relative z-[1] flex flex-row items-center justify-between gap-2 space-y-0 px-3 py-3">
        <div className="min-w-0 flex-1">
          <CardTitle
            className={`hawk-outside-curve-pill inline-flex max-w-full border px-2 py-0.5 text-[8px] font-semibold uppercase leading-tight tracking-wider backdrop-blur-sm sm:text-[9px] ${glass.badge}`}
          >
            {label}
          </CardTitle>
          <p className={`mt-1 text-2xl font-bold tabular-nums tracking-tight sm:text-3xl ${glass.value}`}>{value}</p>
        </div>
      </CardHeader>
    </Card>
  );
}

interface SiteRow {
  id: number;
  url: string;
  topics: SubscriptionTopic[];
  lastScraped: string;
  articleCount: number;
  status: 'active' | 'paused';
  healthStatus: 'never_scraped' | 'success' | 'no_articles' | 'failed';
  lastError: string | null;
  consecutiveFailures: number;
}

type TranslateFn = (key: string, vars?: Record<string, string | number>) => string;

/** Format ISO timestamp into a human-readable "X ago" string */
function timeAgo(isoDate: string | null | undefined, t: TranslateFn): string {
  if (!isoDate) return t('common.never');
  const diff = Date.now() - new Date(isoDate).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return t('common.justNow');
  if (minutes < 60) return t('common.minAgo', { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('common.hoursAgo', { n: hours });
  const days = Math.floor(hours / 24);
  return t('common.daysAgo', { n: days });
}

/** Snap minute to nearest allowed quarter */
function snapMinute(rawMin: string): string {
  return MINUTES.includes(rawMin) ? rawMin : '00';
}

/** Parse "HH:MM" → { h, m } */
function parseTime(val: string | undefined): { h: string; m: string } {
  if (!val) return { h: '07', m: '00' };
  const [a, b] = val.split(':');
  return { h: (a ?? '07').padStart(2, '0'), m: snapMinute((b ?? '00').padStart(2, '0')) };
}

/** Map API scraper source → local SiteRow */
function statusTone(status: SiteRow['healthStatus']): string {
  switch (status) {
    case 'success':
      return 'bg-green-100 text-green-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    case 'no_articles':
      return 'bg-amber-100 text-amber-700';
    default:
      return 'bg-slate-500 text-white';
  }
}

function statusLabel(status: SiteRow['healthStatus'], t: TranslateFn): string {
  switch (status) {
    case 'success':
      return t('common.success');
    case 'failed':
      return t('common.failed');
    case 'no_articles':
      return t('scraperPool.noArticles');
    default:
      return t('scraperPool.unscraped');
  }
}

function apiToSite(s: ScraperSource, t: TranslateFn): SiteRow {
  const lastRunAt = s.last_success_at ?? s.last_scraped ?? s.last_attempted_at;
  return {
    id: s.id,
    url: s.url || '',
    topics: Array.isArray(s.topics) ? s.topics : [],
    lastScraped: s.status === 'paused' ? t('common.paused') : timeAgo(lastRunAt, t),
    articleCount: Number(s.article_count ?? 0),
    status: s.status === 'paused' ? 'paused' : 'active',
    healthStatus: s.last_scrape_status || 'never_scraped',
    lastError: s.last_scrape_error || null,
    consecutiveFailures: Number(s.consecutive_failures ?? 0),
  };
}

type ToastApi = { showSuccess: (msg: string) => void; showError: (msg: string) => void };

/** Normalize the `/moderators/me/scraper-quota` payload into a strict shape. */
function normalizeQuota(raw: unknown): ModeratorScraperQuota {
  const q = (raw ?? {}) as Record<string, unknown>;
  return {
    scraper_enabled: Boolean(q.scraper_enabled),
    max_articles_per_url: Number(q.max_articles_per_url ?? 200),
    max_urls_per_moderator_run: Number(q.max_urls_per_moderator_run ?? 50),
    auto_run_per_day: Number(q.auto_run_per_day ?? 0),
    auto_used_today: Number(q.auto_used_today ?? 0),
    auto_remaining_today: Number(q.auto_remaining_today ?? 0),
    extra_remaining: Number(q.extra_remaining ?? 0),
    extra_runs_limit: Number(q.extra_runs_limit ?? 0),
    active_source_count: Number(q.active_source_count ?? 0),
  };
}

export function ScraperPoolPage() {
  const { moderatorId } = useAuth() as { moderatorId: number | null };
  const { showSuccess, showError } = useToast() as ToastApi;
  const { t } = useUiLocale();

  const [sites, setSites] = useState<SiteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [allTopics, setAllTopics] = useState<SubscriptionTopic[]>([]);
  const [adding, setAdding] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SiteRow | null>(null);
  const [quota, setQuota] = useState<ModeratorScraperQuota | null>(null);

  /* Schedule state */
  const [scheduleHour, setScheduleHour] = useState('07');
  const [scheduleMinute, setScheduleMinute] = useState('00');
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [scheduleSaved, setScheduleSaved] = useState(true);
  const [savingSchedule, setSavingSchedule] = useState(false);

  /* Search + filter + pagination state */
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTopicId, setActiveTopicId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageDirection, setPageDirection] = useState<1 | -1>(1); // 1 = forward, -1 = backward
  const PAGE_SIZE = 5;

  const goToPage = (page: number) => {
    setPageDirection(page > currentPage ? 1 : -1);
    setCurrentPage(page);
  };

  /* Poll ref for scraping status */
  const pollRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number>(0);

  /** Maximum wall-clock time we keep polling (15 minutes). */
  const MAX_POLL_MS = 15 * 60 * 1000;

  /** Stop polling and clear the running indicator. */
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setRunningNow(false);
  }, []);

  /** Start polling until the backend reports a terminal state, completed_at is
   *  set, or MAX_POLL_MS elapses — whichever comes first. */
  const startPolling = useCallback(() => {
    if (pollRef.current) return; // already polling
    pollStartRef.current = Date.now();

    pollRef.current = setInterval(async () => {
      /* Hard timeout — stop polling after MAX_POLL_MS regardless */
      if (Date.now() - pollStartRef.current > MAX_POLL_MS) {
        stopPolling();
        return;
      }

      try {
        const { data } = await scrapingApi.getStatus();
        const status   = data as ScrapingStatus;
        const isDone   =
          status.state === 'end' ||
          /* backend completed but state label lagged */ !!status.completed_at ||
          /* unknown terminal state returned by a future backend version */
          (status.state !== 'start' && status.state !== 'running');

        if (isDone) stopPolling();
      } catch {
        stopPolling();
      }
    }, 2000);
  }, [stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSites = sites.filter((s) => s.status === 'active');
  /** Prefer the backend's own count (may include sources not yet reflected client-side). */
  const activeSourceCount = quota ? Number(quota.active_source_count ?? activeSites.length) : activeSites.length;
  const urlOverLimit =
    quota != null && activeSourceCount > Number(quota.max_urls_per_moderator_run || 0);
  /** At capacity for max URLs/run — green highlight on the quota tile. */
  const urlsAtMax =
    quota != null &&
    activeSourceCount === Number(quota.max_urls_per_moderator_run || 0);
  const quotaLocked = quota != null && !quota.scraper_enabled;
  const noExtraRuns = quota != null && Number(quota.extra_remaining) <= 0;
  const runBlocked = !quota || quotaLocked || noExtraRuns || urlOverLimit || activeSites.length === 0;
  const runBlockedReason = !quota
    ? null
    : quotaLocked
      ? t('scraperPool.quotaLockedMessage')
      : urlOverLimit
        ? t('scraperPool.quotaMaxUrlLimit', { limit: quota.max_urls_per_moderator_run })
        : noExtraRuns
          ? t('scraperPool.quotaNoExtraRuns')
          : null;
  const totalArticles = sites.reduce((sum, s) => sum + s.articleCount, 0);

  /** Topics actually present across the pool (for filter chips). */
  const poolTopics = useMemo(() => {
    const byId = new Map<number, SubscriptionTopic>();
    sites.forEach((s) => s.topics.forEach((t) => byId.set(t.id, t)));
    return Array.from(byId.values()).sort((a, b) => a.topic.localeCompare(b.topic));
  }, [sites]);

  /** Sites after search + topic filter */
  const filteredSites = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return sites.filter((s) => {
      const matchesSearch =
        !q ||
        s.url.toLowerCase().includes(q) ||
        s.topics.some((t) => t.topic.toLowerCase().includes(q));
      const matchesTopic =
        activeTopicId == null || s.topics.some((t) => t.id === activeTopicId);
      return matchesSearch && matchesTopic;
    });
  }, [sites, searchQuery, activeTopicId]);

  const totalPages = Math.max(1, Math.ceil(filteredSites.length / PAGE_SIZE));

  /** Reset to page 1 whenever filter/search changes */
  useEffect(() => { setCurrentPage(1); }, [searchQuery, activeTopicId]);

  /** Current page slice */
  const pagedSites = useMemo(
    () => filteredSites.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredSites, currentPage],
  );

  const pageRangeStart = filteredSites.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageRangeEnd = Math.min(currentPage * PAGE_SIZE, filteredSites.length);

  /* ── Load data on mount ── */
  const loadData = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const [sourcesRes, scheduleRes, statusRes, topicsRes, quotaRes] = await Promise.allSettled([
        scraperSourcesApi.list({}, { signal }),
        moderatorId != null
          ? moderatorSchedulerApi.get(moderatorId, { signal })
          : Promise.reject(new Error('No moderator ID')),
        scrapingApi.getStatus(),
        scraperSourcesApi.listTopics({ signal }),
        scraperQuotaApi.meQuota({ signal }),
      ]);

      /* Scraper sources */
      if (sourcesRes.status === 'fulfilled') {
        const raw = Array.isArray(sourcesRes.value.data) ? sourcesRes.value.data : [];
        setSites(raw.map((s) => apiToSite(s as ScraperSource, t)));
      } else {
        setSites([]);
      }

      /* Moderator topics for the picker */
      if (topicsRes.status === 'fulfilled') {
        const raw = Array.isArray(topicsRes.value.data) ? topicsRes.value.data : [];
        setAllTopics(raw as SubscriptionTopic[]);
      }

      /* Scheduler */
      if (scheduleRes.status === 'fulfilled') {
        const sch = scheduleRes.value.data as ClientScheduler;
        const { h, m } = parseTime(sch.morning_time);
        setScheduleHour(h);
        setScheduleMinute(m);
        setScheduleEnabled(!!sch.is_enabled);
        setScheduleSaved(true);
      }

      /* Scraping status — resume running indicator if still active */
      if (statusRes.status === 'fulfilled') {
        const state = (statusRes.value.data as ScrapingStatus).state;
        const isActive = state === 'start' || state === 'running';
        if (isActive) {
          setRunningNow(true);
          startPolling();
        }
      }

      if (quotaRes.status === 'fulfilled') {
        setQuota(normalizeQuota(quotaRes.value.data));
      } else {
        setQuota(null);
      }
    } catch {
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, [moderatorId, startPolling, t]);

  useEffect(() => {
    const ac = new AbortController();
    loadData(ac.signal);
    return () => {
      ac.abort();
      stopPolling();
    };
  }, [loadData, stopPolling]);

  const handleAdd = async () => {
    const url = newUrl.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!url) return;
    if (sites.some((s) => s.url.toLowerCase() === url.toLowerCase())) {
      showError(t('scraperPool.toastExists'));
      return;
    }
    setAdding(true);
    try {
      const { data } = await scraperSourcesApi.create(
        { url, topic_ids: [], status: 'active' },
        {},
      );
      setSites((prev) => [...prev, apiToSite(data as ScraperSource, t)]);
      setNewUrl('');
      showSuccess(t('scraperPool.toastAdded'));
    } catch {
      showError(t('scraperPool.toastAddFailed'));
    } finally {
      setAdding(false);
    }
  };

  const toggleStatus = async (id: number) => {
    /* Optimistic */
    setSites((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s)),
    );
    try {
      const { data } = await scraperSourcesApi.toggle(id, {});
      const updated = data as ScraperSource;
      setSites((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, status: updated.status === 'paused' ? 'paused' : 'active' }
            : s,
        ),
      );
    } catch {
      /* Revert on error */
      setSites((prev) =>
        prev.map((s) => (s.id === id ? { ...s, status: s.status === 'active' ? 'paused' : 'active' } : s)),
      );
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    try {
      await scraperSourcesApi.remove(id, {});
      setSites((prev) => prev.filter((s) => s.id !== id));
      setDeleteTarget(null);
      showSuccess(t('scraperPool.toastRemoved'));
    } catch {
      setDeleteTarget(null);
      showError(t('scraperPool.toastRemoveFailed'));
    }
  };

  const handleRunNow = async () => {
    if (moderatorId == null) return;
    if (runBlocked) {
      if (runBlockedReason) showError(runBlockedReason);
      return;
    }
    setRunningNow(true);
    try {
      /* Quota-aware: no max_pages override — the backend applies the
         client's configured scraper limits (auto/extra runs, max URLs). */
      const res = await moderatorSchedulerApi.runNow(moderatorId, undefined, {});
      if (res?.data?.accepted === false) {
        setRunningNow(false);
        showError(
          (typeof res.data.message === 'string' && res.data.message.trim())
            || t('scraperPool.toastRunFailed'),
        );
        try {
          const q = await scraperQuotaApi.meQuota({});
          setQuota(normalizeQuota(q.data));
        } catch {
          /* ignore */
        }
        return;
      }
      startPolling();
      /* Refresh remaining quota so badges reflect the run we just consumed. */
      try {
        const q = await scraperQuotaApi.meQuota({});
        setQuota(normalizeQuota(q.data));
      } catch {
        /* Non-fatal — badges just stay stale until the next refresh. */
      }
    } catch (e: unknown) {
      setRunningNow(false);
      const ax = e as { response?: { status?: number; data?: { detail?: unknown } } };
      const detail = ax.response?.data?.detail;
      if (ax.response?.status === 403 && detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const d = detail as ScraperQuotaErrorDetail;
        showError(d.message || t('scraperPool.toastRunFailed'));
      } else if (ax.response?.status === 409 && typeof detail === 'string' && detail.trim()) {
        showError(detail);
      } else if (typeof detail === 'string' && detail.trim()) {
        showError(detail);
      } else {
        showError(t('scraperPool.toastRunFailed'));
      }
      try {
        const q = await scraperQuotaApi.meQuota({});
        setQuota(normalizeQuota(q.data));
      } catch {
        /* ignore */
      }
    }
  };

  const handleSaveSchedule = async () => {
    if (moderatorId == null) return;
    setSavingSchedule(true);
    try {
      await moderatorSchedulerApi.put(
        moderatorId,
        { morning_time: `${scheduleHour}:${scheduleMinute}`, is_enabled: scheduleEnabled },
        {},
      );
      setScheduleSaved(true);
      showSuccess(t('common.saved'));
    } catch {
      showError(t('scraperPool.toastSaveFailed'));
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleRefresh = () => {
    loadData();
  };

  if (loading) {
    return (
      <div className="hawk-scrollbar-rainbow flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto bg-transparent">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="hawk-admin-surface flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <header className="hawk-glass-header hawk-glass-header--login shrink-0">
        <div className="px-2 py-2 sm:px-3 sm:py-2.5">
          <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight sm:text-2xl md:text-3xl">
                <span className="hawk-gradient-text">{t('scraperPool.title')}</span>
              </h1>
              <p className="hawk-page-subtitle mt-0.5 text-xs leading-snug sm:text-sm">
                {t('scraperPool.subtitle')}
              </p>
              {quotaLocked && (
                <div className="mt-2">
                  <Badge className="gap-1 border-0 bg-rose-100 text-rose-800">
                    <Lock className="h-3 w-3" /> {t('scraperPool.quotaLocked')}
                  </Badge>
                </div>
              )}
            </div>
            <div className="flex w-full shrink-0 flex-col items-stretch gap-1.5 sm:w-auto sm:max-w-xs sm:items-end">
              <Button
                onClick={handleRunNow}
                disabled={runningNow || runBlocked}
                className="w-full shrink-0 gap-2 bg-sky-700 shadow-[0_4px_14px_-4px_rgba(3,105,161,0.45)] hover:bg-sky-800 text-white sm:w-auto"
              >
                {runningNow ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {t('scraperPool.running')}</>
                ) : (
                  <><Play className="w-4 h-4" /> {t('scraperPool.runNow')}</>
                )}
              </Button>
              {runBlockedReason && (
                <p className="text-left text-[11px] leading-snug text-rose-600 sm:text-right">
                  {runBlockedReason}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="hawk-scrollbar-rainbow w-full min-w-0 min-h-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto px-2 pb-3 pt-0 sm:space-y-6 sm:px-1 sm:pb-3">

        {/* Run now indicator */}
        <AnimatePresence>
          {runningNow && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex items-start gap-3 rounded-xl border border-white/50 bg-purple-500/10 px-3 py-3 shadow-[0_4px_20px_-6px_rgba(147,51,234,0.2)] ring-1 ring-purple-200/50 backdrop-blur-xl sm:items-center sm:px-4">
                <Loader2 className="mt-0.5 w-4 h-4 flex-shrink-0 animate-spin text-purple-600 sm:mt-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-purple-900">{t('scraperPool.scraperRunning')}</p>
                  <p className="text-xs leading-relaxed text-purple-700">
                    Collecting articles from {activeSites.length} active source{activeSites.length !== 1 ? 's' : ''}. Results will appear in the Data Pool.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={stopPolling}
                  className="text-xs text-purple-500 hover:text-purple-800 underline underline-offset-2 transition-colors flex-shrink-0"
                  title="Dismiss this indicator (does not stop the backend job)"
                >
                  {t('common.dismiss')}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid w-full grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-3">
          {/* ── Left column: Stats + Add Source ── */}
          <div className="flex min-h-0 min-w-0 flex-col gap-4 lg:col-span-1">
            <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
              <ScraperStatCard label={t('scraperPool.statTotal')} value={sites.length} icon={Link} tone="purple" />
              <ScraperStatCard label={t('scraperPool.statActive')} value={activeSites.length} icon={CheckCircle2} tone="green" />
              <ScraperStatCard label={t('scraperPool.statArticles')} value={totalArticles.toLocaleString()} icon={BarChart2} tone="indigo" />
            </div>

            {/* Add Source form */}
            <Card className={`relative flex flex-col gap-0 overflow-visible ${GLASS_PANEL} ring-1 ring-indigo-200/35`}>
              <CardGlowBackground variant="indigo" />
              <CardHeader className="relative z-[1] flex-shrink-0 pb-2 pt-4">
                <CardTitle className="flex items-center gap-2 text-base text-slate-900">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500/15 ring-1 ring-indigo-200/50">
                    <Plus className="h-4 w-4 text-indigo-600" />
                  </span>
                  {t('scraperPool.addSource')}
                </CardTitle>
                <CardDescription className="text-xs text-slate-500">{t('scraperPool.addSourceDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="relative z-[1] min-h-0 flex-1 space-y-4 overflow-visible pb-4">
                <div className="space-y-1.5">
                  <Label htmlFor="url" className="text-xs font-medium">{t('scraperPool.domainLabel')}</Label>
                  <div className="relative">
                    <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
                    <Input
                      id="url"
                      placeholder={t('scraperPool.domainPlaceholder')}
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
                      className="pl-8 text-sm"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">{t('scraperPool.domainHint')}</p>
                </div>
                <Button
                  type="button"
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleAdd}
                  disabled={!newUrl.trim() || adding}
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {t('scraperPool.addToPool')}
                </Button>
                <Separator />
                <div className="space-y-1 rounded-lg border border-white/50 bg-white/30 px-2.5 py-2 text-xs text-slate-600 backdrop-blur-sm">
                  <p className="font-medium text-slate-700">{t('scraperPool.tips')}</p>
                  <p>• Add 10–100 URLs for best coverage</p>
                  <p>• Tag sources with topics to organise your pool</p>
                  <p>• Paused URLs won't be scraped</p>
                </div>
              </CardContent>
            </Card>

            <ImportSourcesCard onImportComplete={loadData} />
          </div>

          {/* ── Right column: Daily Schedule + Your Sources ── */}
          <div className="flex min-h-0 min-w-0 flex-col gap-4 lg:col-span-2">
          <Card className={`relative flex min-h-0 w-full flex-col gap-0 overflow-hidden ${GLASS_PANEL} ring-1 ring-purple-200/40`}>
            <CardGlowBackground variant="purple" />

            <div className="relative z-[1] grid min-h-0 grid-cols-1 items-stretch divide-y divide-white/50 md:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] md:divide-x md:divide-y-0">
              {/* Column 1 — Daily Schedule: stretch to Pool status height; footer pins with MAX ARTICLES row */}
              <CardContent className="flex h-full min-h-0 flex-col gap-2 p-3 pb-3 [&:last-child]:pb-3">
                <div className="flex shrink-0 items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple-500/15 ring-1 ring-purple-200/50">
                      <CalendarClock className="h-3.5 w-3.5 text-purple-600" strokeWidth={2} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{t('scraperPool.dailySchedule')}</p>
                      <p className="text-[11px] text-slate-500">{t('scraperPool.autoScrapeTime')}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setScheduleEnabled((v) => !v); setScheduleSaved(false); }}
                    className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 ${
                      scheduleEnabled ? 'bg-purple-600' : 'bg-gray-200'
                    }`}
                    title={scheduleEnabled ? 'Disable schedule' : 'Enable schedule'}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        scheduleEnabled ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <div className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 rounded-xl border border-white/45 bg-white/30 px-2.5 py-4 text-center backdrop-blur-sm">
                  {quota && (
                    <span
                      className={`absolute right-1.5 top-1.5 inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[11px] font-bold tabular-nums leading-none ${
                        Number(quota.auto_remaining_today) > 0
                          ? 'bg-purple-100/90 text-purple-700'
                          : 'bg-rose-100/90 text-rose-700'
                      }`}
                      title={t('scraperPool.autoRunsRemainingHint')}
                    >
                      {Number(quota.auto_remaining_today)}
                      <span className="mx-0.5 font-semibold opacity-50">/</span>
                      {Number(quota.auto_run_per_day)}
                    </span>
                  )}
                  <p
                    className={`font-mono text-3xl font-bold leading-none tabular-nums tracking-tight sm:text-4xl ${
                      scheduleEnabled ? 'text-purple-700' : 'text-slate-300'
                    }`}
                  >
                    {scheduleEnabled ? `${scheduleHour}:${scheduleMinute}` : t('common.off')}
                  </p>
                  {scheduleEnabled && (
                    <p className="text-[11px] text-slate-500">{t('scraperPool.runsDaily')}</p>
                  )}
                  <div
                    className={`mt-0.5 flex flex-wrap items-center justify-center gap-1.5 transition-opacity ${
                      scheduleEnabled ? 'opacity-100' : 'pointer-events-none opacity-40'
                    }`}
                  >
                    <Select
                      value={scheduleHour}
                      onValueChange={(v) => { setScheduleHour(v); setScheduleSaved(false); }}
                    >
                      <SelectTrigger className="h-8 w-[3.5rem] rounded-lg border-white/60 bg-white/70 px-1.5 font-mono text-sm shadow-sm backdrop-blur-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.map((h) => (
                          <SelectItem key={h} value={h} className="font-mono">{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-base font-bold text-slate-400">:</span>
                    <Select
                      value={scheduleMinute}
                      onValueChange={(v) => { setScheduleMinute(v); setScheduleSaved(false); }}
                    >
                      <SelectTrigger className="h-8 w-[3.5rem] rounded-lg border-white/60 bg-white/70 px-1.5 font-mono text-sm shadow-sm backdrop-blur-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MINUTES.map((m) => (
                          <SelectItem key={m} value={m} className="font-mono">{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Height/padding mirrors Pool status metric tiles so Saved sits on the value row */}
                <div className="mt-auto flex shrink-0 items-end justify-between gap-2 border-t border-white/50 pt-2">
                  <div className="flex min-w-0 items-center gap-1.5 self-end pb-0.5 text-xs text-slate-500">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-purple-500" />
                    <span>
                      {t('scraperPool.activePaused', {
                        active: activeSites.length,
                        paused: sites.filter((s) => s.status === 'paused').length,
                      })}
                    </span>
                  </div>
                  <AnimatePresence mode="wait">
                    {scheduleSaved && !savingSchedule ? (
                      <motion.span
                        key="saved"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        className="mb-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> {t('common.saved')}
                      </motion.span>
                    ) : (
                      <motion.div key="save-btn" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mb-0.5">
                        <Button
                          className="h-7 gap-1.5 rounded-full px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                          onClick={handleSaveSchedule}
                          disabled={savingSchedule || moderatorId == null}
                        >
                          {savingSchedule ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Save className="h-3.5 w-3.5" />
                          )}
                          {t('common.save')}
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {moderatorId == null && (
                  <p className="text-center text-[11px] text-amber-700">{t('scraperPool.loginToSave')}</p>
                )}
              </CardContent>

              {/* Column 2 — Pool status: bottom metrics row shares baseline with Saved */}
              <CardContent className="flex h-full min-h-0 flex-col gap-2 p-3 pb-3 [&:last-child]:pb-3">
                <div className="flex shrink-0 min-w-0 items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-500/15 ring-1 ring-indigo-200/50">
                      <BarChart2 className="h-3.5 w-3.5 text-indigo-600" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-800">{t('scraperPool.poolStatus')}</p>
                      <p className="text-[11px] text-slate-500">{t('scraperPool.poolStatusDesc')}</p>
                    </div>
                  </div>
                  {runningNow ? (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-purple-700">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('scraperPool.scraperRunning')}
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-emerald-700">
                      <CheckCircle2 className="h-3 w-3" />
                      {t('scraperPool.statusIdle')}
                    </span>
                  )}
                </div>

                {/* Metrics stack pins to bottom so MAX ARTICLES / URL shares a row with Saved */}
                <div className="mt-auto flex min-h-0 flex-col gap-1.5">
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="rounded-xl border border-white/45 bg-white/30 px-2.5 py-2 backdrop-blur-sm">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{t('scraperPool.statActive')}</p>
                      <p className="mt-0.5 text-lg font-bold tabular-nums text-emerald-800">{activeSites.length}</p>
                    </div>
                    <div className="rounded-xl border border-white/45 bg-white/30 px-2.5 py-2 backdrop-blur-sm">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{t('scraperPool.statArticles')}</p>
                      <p className="mt-0.5 text-lg font-bold tabular-nums text-indigo-800">{totalArticles.toLocaleString()}</p>
                    </div>
                  </div>
                  {quota ? (
                    <div className="grid grid-cols-2 gap-1.5">
                      <div
                        className={`rounded-xl border px-2.5 py-2 backdrop-blur-sm ${
                          noExtraRuns
                            ? 'border-rose-200/70 bg-rose-50/50'
                            : 'border-white/45 bg-white/30'
                        }`}
                      >
                        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          {t('scraperPool.quotaExtraRemaining')}
                        </p>
                        <p
                          className={`mt-0.5 font-mono text-lg font-bold tabular-nums ${
                            noExtraRuns ? 'text-rose-700' : 'text-sky-800'
                          }`}
                        >
                          {quota.extra_remaining}/{quota.extra_runs_limit}
                        </p>
                      </div>
                      <div
                        className={`rounded-xl border px-2.5 py-2 backdrop-blur-sm ${
                          urlOverLimit
                            ? 'border-rose-200/70 bg-rose-50/50'
                            : urlsAtMax
                              ? 'border-emerald-200/70 bg-emerald-50/50'
                              : 'border-white/45 bg-white/30'
                        }`}
                      >
                        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          {t('scraperPool.quotaMaxUrls')}
                        </p>
                        <p
                          className={`mt-0.5 font-mono text-lg font-bold tabular-nums ${
                            urlOverLimit
                              ? 'text-rose-700'
                              : urlsAtMax
                                ? 'text-emerald-700'
                                : 'text-slate-800'
                          }`}
                        >
                          {activeSourceCount}/{quota.max_urls_per_moderator_run}
                        </p>
                      </div>
                      <div className="rounded-xl border border-white/45 bg-white/30 px-2.5 py-2 backdrop-blur-sm">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          {t('scraperPool.quotaMaxArticles')}
                        </p>
                        <p className="mt-0.5 font-mono text-lg font-bold tabular-nums text-slate-800">
                          {quota.max_articles_per_url}
                        </p>
                      </div>
                      <div className="rounded-xl border border-white/45 bg-white/30 px-2.5 py-2 backdrop-blur-sm">
                        <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                          {t('scraperPool.nextRun')}
                        </p>
                        <p className="mt-0.5 font-mono text-lg font-bold tabular-nums text-slate-800">
                          {scheduleEnabled ? `${scheduleHour}:${scheduleMinute}` : t('common.off')}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-white/45 bg-white/30 px-2.5 py-2 backdrop-blur-sm">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                        {t('scraperPool.nextRun')}
                      </p>
                      <p className="mt-0.5 font-mono text-lg font-bold tabular-nums text-slate-800">
                        {scheduleEnabled ? `${scheduleHour}:${scheduleMinute}` : t('common.off')}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </div>
          </Card>

          {/* ── Sites table ── */}
          <Card className={`relative flex min-h-0 min-w-0 flex-col gap-0 overflow-hidden ${GLASS_PANEL} ring-1 ring-blue-200/35`}>
            <CardHeader className="relative z-[1] flex-shrink-0 space-y-1.5 pb-1.5 pt-4">
            <CardGlowBackground variant="blue" />
            
              <div className="flex flex-col gap-1.5 min-[520px]:flex-row min-[520px]:items-center min-[520px]:justify-between">
                <div className="min-w-0">
                  <CardTitle className="flex items-center gap-2 text-base text-slate-900">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 ring-1 ring-blue-200/50">
                      <Globe className="h-4 w-4 text-blue-600" />
                    </span>
                    {t('scraperPool.yourSources')}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {filteredSites.length !== sites.length
                      ? `Showing ${filteredSites.length} of ${sites.length} URL${sites.length !== 1 ? 's' : ''}`
                      : `${sites.length} URL${sites.length !== 1 ? 's' : ''} in your pool`}
                    {filteredSites.length > 0 && (
                      <span className="mt-0.5 block text-[11px] text-slate-500">
                        {PAGE_SIZE} per page — use Previous / Next to switch pages
                      </span>
                    )}
                  </CardDescription>
                </div>
                <div className="flex w-full shrink-0 items-center gap-2 min-[520px]:w-auto min-[520px]:max-w-[30rem]">
                  <div className="relative min-w-0 flex-1 min-[520px]:w-[22rem] min-[520px]:flex-none">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      placeholder={t('scraperPool.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full rounded-lg border border-white/60 bg-white/60 py-1.5 pl-8 pr-8 text-sm shadow-sm backdrop-blur-sm transition-colors focus:border-blue-300/80 focus:bg-white/80 focus:outline-none focus:ring-1 focus:ring-blue-200/60"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-400"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 gap-1.5 border-red-200 bg-red-100 px-2.5 text-xs text-red-700 hover:border-red-700 hover:bg-red-700 hover:text-white"
                    onClick={handleRefresh}
                  >
                    <RefreshCw className="h-3 w-3" /> Refresh
                  </Button>
                </div>
              </div>

              {/* Topic filter chips */}
              <div className="space-y-1.5">
                {poolTopics.length > 1 && (
                  <div className="flex items-center gap-1 flex-wrap">
                    <Filter className="w-3 h-3 text-slate-500 flex-shrink-0" />
                    <button
                      type="button"
                      onClick={() => setActiveTopicId(null)}
                      className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full border transition-colors ${
                        activeTopicId == null
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {t('common.all')} ({sites.length})
                    </button>
                    {poolTopics.map((topic) => {
                      const count = sites.filter((s) =>
                        s.topics.some((x) => x.id === topic.id),
                      ).length;
                      const isActive = activeTopicId === topic.id;
                      return (
                        <button
                          key={topic.id}
                          type="button"
                          onClick={() => setActiveTopicId(isActive ? null : topic.id)}
                          style={isActive ? topicChipStyle(topic.color) : undefined}
                          className={`text-[11px] font-medium px-1.5 py-0.5 rounded-full border transition-colors ${
                            isActive
                              ? 'border-transparent'
                              : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {topic.topic} ({count})
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardHeader>

            {/* Table — fixed 5 rows per page */}
            <CardContent className="relative z-[1] min-h-0 min-w-0 p-0">
              <div className="hawk-scrollbar-rainbow min-h-0 min-w-0 overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]">
                <table className="w-full min-w-[36rem] table-fixed text-sm sm:min-w-[42rem]">
                  <thead>
                    <tr className="border-t border-white/50 bg-white/45 text-left text-slate-600 backdrop-blur-md">
                      <th className="w-[28%] px-4 py-2 text-xs font-medium">{t('scraperPool.colUrl')}</th>
                      <th className="w-[12%] px-2.5 py-2 text-xs font-medium">{t('scraperPool.colTopics')}</th>
                      <th className="w-[14%] px-2.5 py-2 text-xs font-medium">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('scraperPool.colLastRun')}</span>
                      </th>
                      <th className="w-[26%] px-2.5 py-2 text-xs font-medium">{t('scraperPool.colResult')}</th>
                      <th className="w-[10%] px-2.5 py-2 text-right text-xs font-medium">{t('scraperPool.colArticles')}</th>
                      <th className="w-[10%] px-2.5 py-2 text-right text-xs font-medium">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <AnimatePresence mode="wait" custom={pageDirection}>
                    <motion.tbody
                      key={`page-${currentPage}`}
                      custom={pageDirection}
                      variants={{
                        enter: (dir: number) => ({ x: dir * 48, opacity: 0 }),
                        center: { x: 0, opacity: 1 },
                        exit:  (dir: number) => ({ x: dir * -48, opacity: 0 }),
                      }}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    >
                      {/* Real rows */}
                      {pagedSites.map((s) => (
                        <tr
                          key={s.id}
                          className="h-[3.75rem] border-t border-white/40 transition-colors hover:bg-white/35"
                        >
                          <td className="align-middle px-4 py-1.5">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${s.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <span className={`min-w-0 truncate text-xs font-medium ${s.status === 'paused' ? 'text-slate-500' : 'text-slate-800'}`} title={s.url}>
                                {s.url}
                              </span>
                            </div>
                          </td>
                          <td className="align-middle px-2.5 py-1.5">
                            {(() => {
                              /* Resolve full topic display data (name + color) from allTopics
                                 by id — the list response only carries minimal topic refs. */
                              const rowTopics = s.topics
                                .map((t) => allTopics.find((a) => a.id === t.id) ?? t)
                                .filter(Boolean) as SubscriptionTopic[];
                              if (rowTopics.length === 0) {
                                return <span className="text-[11px] text-slate-400">—</span>;
                              }
                              return (
                                <div className="flex flex-wrap gap-0.5">
                                  {rowTopics.slice(0, 3).map((t) => (
                                    <span
                                      key={t.id}
                                      style={topicChipStyle(t.color)}
                                      className="truncate rounded-full border px-1.5 py-0 text-[10px] font-medium leading-5"
                                    >
                                      {t.topic}
                                    </span>
                                  ))}
                                  {rowTopics.length > 3 && (
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0 text-[10px] font-medium text-slate-500 leading-5">
                                      +{rowTopics.length - 3}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="align-middle px-2.5 py-1.5 text-xs text-slate-500">
                            {s.status === 'paused' ? (
                              <span className="text-[11px] italic text-slate-500">{t('common.paused')}</span>
                            ) : (
                              <div className="min-w-0">

                                <p className="truncate text-[11px]">{s.lastScraped}</p>
                                {s.consecutiveFailures > 0 && (
                                  <p className="truncate text-[10px] text-red-500">
                                    {t('scraperPool.failCount', { n: s.consecutiveFailures })}
                                  </p>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="align-middle px-2.5 py-1.5">
                            <div className="min-w-0">
                              <Badge className={`text-[10px] font-medium border-0 px-1.5 py-0 ${statusTone(s.healthStatus)}`}>
                                {statusLabel(s.healthStatus, t)}
                              </Badge>
                              {s.lastError && s.status !== 'paused' && (
                                <p className="mt-0.5 line-clamp-2 text-[10px] leading-tight text-red-500" title={s.lastError}>
                                  {s.lastError}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="align-middle px-2.5 py-1.5 text-right">
                            <span className={`text-xs font-semibold ${s.articleCount > 0 ? 'text-indigo-600' : 'text-slate-500'}`}>
                              {s.articleCount > 0 ? s.articleCount.toLocaleString() : '—'}
                            </span>
                          </td>
                          <td className="align-middle px-2.5 py-1.5">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => toggleStatus(s.id)}
                                className={`p-1.5 rounded transition-colors ${s.status === 'active' ? 'hover:bg-amber-50 text-slate-500 hover:text-amber-600' : 'hover:bg-green-50 text-slate-500 hover:text-green-600'}`}
                                title={s.status === 'active' ? 'Pause' : 'Resume'}
                              >
                                {s.status === 'active' ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteTarget(s)}
                                className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-500 transition-colors"
                                title={t('common.remove')}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Ghost rows — pad to exactly PAGE_SIZE data rows */}
                      {sites.length > 0 &&
                        filteredSites.length > 0 &&
                        Array.from({ length: PAGE_SIZE - pagedSites.length }).map((_, i) => (
                          <tr key={`ghost-${i}`} className="h-[3.75rem] border-t border-slate-100/80" aria-hidden="true">
                            <td className="px-4 py-1.5" colSpan={6} />
                          </tr>
                        ))}

                      {/* Empty states */}
                      {sites.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-10 text-center">
                            <Globe className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">{t('scraperPool.emptySources')}</p>
                          </td>
                        </tr>
                      )}
                      {sites.length > 0 && filteredSites.length === 0 && (
                        <tr>
                          <td colSpan={6} className="px-4 py-7 text-center">
                            <Search className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                            <p className="text-sm text-slate-500">{t('scraperPool.emptySearch')}</p>
                            <button type="button" onClick={() => { setSearchQuery(''); setActiveTopicId(null); }} className="mt-1 text-xs text-purple-600 hover:underline">
                              {t('common.clearFilters')}
                            </button>
                          </td>
                        </tr>
                      )}
                    </motion.tbody>
                  </AnimatePresence>
                </table>
              </div>

              {/* Pagination — Previous / Next (5 URLs per page) */}
              {sites.length > 0 && filteredSites.length > 0 && (
                <div className="flex flex-col gap-2 border-t border-white/50 bg-white/40 px-3 py-2 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between sm:px-4">
                  <p className="order-2 text-[11px] text-slate-500 sm:order-1">
                    <span className="font-medium text-slate-600">
                      {t('scraperPool.rangeOf', {
                        from: pageRangeStart,
                        to: pageRangeEnd,
                        total: filteredSites.length,
                      })}
                    </span>
                    {filteredSites.length !== sites.length ? (
                      <>
                        {' '}
                        · {filteredSites.length}{' '}
                        <button
                          type="button"
                          onClick={() => { setSearchQuery(''); setActiveTopicId(null); }}
                          className="text-purple-600 hover:underline"
                        >
                          {t('common.clearFilters')}
                        </button>
                      </>
                    ) : (
                      <>
                        {' '}
                        · {t('scraperPool.activePaused', {
                          active: activeSites.length,
                          paused: sites.filter((s) => s.status === 'paused').length,
                        })}
                      </>
                    )}
                  </p>

                  <div className="order-1 flex flex-wrap items-center justify-end gap-2 sm:order-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={() => goToPage(currentPage - 1)}
                      className="h-8 gap-1 border-slate-200 bg-white px-2.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">{t('common.previous')}</span>
                    </Button>
                    <span className="text-xs tabular-nums text-slate-600">
                      {t('scraperPool.pageOf', { page: currentPage, total: totalPages })}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={() => goToPage(currentPage + 1)}
                      className="h-8 gap-1 border-slate-200 bg-white px-2.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    >
                      <span className="hidden sm:inline">{t('common.next')}</span>
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
      </div>

      {/* ── Delete Confirm ── */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-4 h-4" /> {t('scraperPool.removeTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span
                className="block max-h-20 overflow-y-auto rounded-lg border border-red-100 bg-red-50/70 px-2.5 py-2 font-mono text-[11px] font-semibold leading-relaxed text-red-800 break-all"
                title={deleteTarget?.url}
              >
                {deleteTarget?.url}
              </span>
              <span className="block">{t('scraperPool.removeNote')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              {t('common.remove')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
